import { useEditor } from "@craftjs/core";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { BaseReactPlayerProps } from "react-player/base";
import ReactPlayer from "react-player/lazy";
import styled from "styled-components";

import { PlayerOverlay } from "@/components/video-player";
import useAnalytics from "@/hooks/use-analytics";
import togglePageScrollLock from "@/utils/toggle-page-scroll-lock";

import PlayIcon from "./assets/playIcon";
import { Caption, Thumbnail } from "./styles";

type EmbedVideoCardProps = {
  url: string;
  playerOptions?: BaseReactPlayerProps;
  aspectRatio?: "square" | "wide";
  title?: string;
  thumbnail?: string;
  showCaption?: boolean;
};

export const EmbedVideoCard = (props: EmbedVideoCardProps) => {
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const { logAnalytics } = useAnalytics();
  const {
    url,
    playerOptions = {},
    aspectRatio = "wide",
    title,
    thumbnail,
    showCaption = false,
  } = props;

  const { isEnabled } = useEditor((state) => ({
    isEnabled: state.isEnabled(),
  }));

  useEffect(
    function preventScrollOnPlay() {
      togglePageScrollLock({
        method: isPlayerOpen ? "add" : "remove",
        withOffset: false,
      });
    },
    [isPlayerOpen]
  );

  return (
    <>
      <RelativeContainer>
        {thumbnail ? (
          <Thumbnail
            $aspectRatio={aspectRatio}
            alt={title || ""}
            src={thumbnail}
          />
        ) : (
          <ReactPlayer
            url={url}
            controls
            width="100%"
            height="100%"
            {...playerOptions}
            style={{
              pointerEvents: "none",
            }}
          />
        )}
        {showCaption && <Caption>{title || ""}</Caption>}
        <PlayButton
          onClick={() => !isEnabled && setIsPlayerOpen(true)}
          $playable={!isEnabled}
        >
          {thumbnail && (
            <>
              <span className="sr-only">Play video</span>
              {!isPlayerOpen && <PlayIcon />}
            </>
          )}
        </PlayButton>
      </RelativeContainer>
      {isPlayerOpen &&
        createPortal(
          <PlayerOverlay
            onClose={() => {
              setIsPlayerOpen(false);
              if (currentTime <= 0) return;

              logAnalytics("CANVAS_EMBEDDED_VIDEO_PLAYED", {
                url,
                currentTime,
              });
            }}
          >
            <ReactPlayer
              url={url}
              controls
              playing
              width="100%"
              height="100%"
              {...playerOptions}
              onProgress={(s) => setCurrentTime(s.playedSeconds)}
              style={{
                pointerEvents: isEnabled ? "none" : "initial",
              }}
            />
          </PlayerOverlay>,
          document.body
        )}
    </>
  );
};

const RelativeContainer = styled.div`
  position: relative;
  height: 100%;
`;

const PlayButton = styled.button<{ $playable: boolean }>`
  --icon-size: 60px;
  position: absolute;
  top: calc(50% - (var(--icon-size) / 2));
  left: calc(50% - (var(--icon-size) / 2));
  width: var(--icon-size);
  height: var(--icon-size);

  &:hover:not([disabled]) {
    cursor: pointer;
  }

  & > svg {
    width: var(--icon-size);
    height: var(--icon-size);
  }
`;
